import styled from 'styled-components'
import { RADOM_COLORS } from '../../util/Constants'
import { AssociatedEntityType, RadomEvent } from '../../state/Events'
import { getTransactionActivityUrl } from '../../util/TransactionLinks'
import { formatCurrency } from '../../util/Util'
import { getMethod } from '../../util/Managed'
import NewSubscription from '../../icons/NewSubscription'
import OrderIcon from '../../icons/Order'
import Refund from '../../icons/Refund'
import SubscriptionRevoked from '../../icons/SubscriptionRevoked'
import WithdrawalIcon from '../../icons/Withdrawal'
import DepositIcon from '../../icons/Deposit'
import React from 'react'
export type NotificationTone = 'in' | 'out' | 'blue' | 'neutral' | 'warning'

const getPaymentNotificationItem = (
  event: Extract<RadomEvent, { name: 'PaymentEvent' | 'RecurringPaymentEvent' | 'IncompletePaymentEvent' | 'SubscriptionPaymentEvent' | 'OpenBanking' }>,
  unread: boolean
): NotificationViewModel => {
  const methodLabel = getPaymentMethodLabel(event.paymentMethod)
  const openBankingData = event.openBankingPaymentData
  const amountLabel = event.paymentSummary?.grossAmount
    ? formatCurrency(Number(event.paymentSummary.grossAmount), event.paymentSummary.currency)
    : formatAssetNotificationAmount(event.amount, event.paymentMethod.ticker)
  const entityLabel = getAssociatedEntityLabel(event.associatedEntity?.entityType)
  const paymentReference = openBankingData?.paymentReference ??
    openBankingData?.bankStatementReference ??
    openBankingData?.remittanceInformation ??
    openBankingData?.endToEndId
  const senderAccount = openBankingData?.senderIban ??
    openBankingData?.senderAccountNumber ??
    openBankingData?.senderBban ??
    openBankingData?.payerFinancialAddress?.iban?.iban ??
    openBankingData?.payerFinancialAddress?.bankCode?.accountNumber
  const senderName = openBankingData?.senderName ??
    openBankingData?.payerFinancialAddress?.iban?.accountHolderName ??
    openBankingData?.payerFinancialAddress?.bankCode?.accountHolderName
  const senderAddress = event.transactions
    ?.flatMap(transaction => transaction.senderAddresses ?? [])
    .map(sender => sender.address)
    .find(Boolean)
  const railLabel = formatFiatNotificationRailLabel(openBankingData?.paymentScheme)
  const isOpenBanking = event.name === 'OpenBanking'

  return {
    title: isOpenBanking ? 'Open banking payment' : event.label,
    meta: isOpenBanking
      ? `${entityLabel} settled${openBankingData?.bankName ? ` via ${openBankingData.bankName}` : ''}`
      : `${entityLabel} settled via ${methodLabel}`,
    details: compactNotificationDetails([
      isOpenBanking ? railLabel : methodLabel,
      openBankingData?.bankName,
      senderName ? `From ${senderName}` : undefined,
      senderAccount ? `Account ${senderAccount}` : undefined,
      paymentReference ? `Ref ${paymentReference}` : undefined,
      senderAddress ? `Sender ${senderAddress}` : undefined
    ]),
    amountLabel,
    tone: event.name === 'IncompletePaymentEvent' ? 'warning' : 'in',
    url: getNotificationUrl(event),
    unread,
    icon: getNotificationIcon(event),
    methodLogo: isOpenBanking ? undefined : event.paymentMethod.logo
  }
}

const getDepositNotificationItem = (
  event: Extract<RadomEvent, { name: 'Deposit' }>,
  unread: boolean
): NotificationViewModel => {
  const crypto = event.data.depositMethod.Crypto
  const fiat = event.data.depositMethod.Fiat
  const details = event.data.beneficiaryDetails
  const method = crypto
    ? getMethod(crypto.network, crypto.token)
    : getMethod(fiat?.onrampNetwork || 'Fiat', fiat?.onrampToken || fiat?.fiatCurrency)
  const amountLabel = fiat
    ? `+${formatCurrency(event.data.fiatGrossAmount ?? event.data.grossAmount, fiat.fiatCurrency)}`
    : formatAssetNotificationAmount(event.data.grossAmount, method.ticker, '+')
  const railLabel = fiat ? inferDepositNotificationRailLabel(event) : undefined
  const senderName = details?.sender_name ?? details?.originator_name
  const senderAccount = details?.sender_iban ??
    details?.originator_iban ??
    details?.iban ??
    details?.sender_account_number
  const reference = details?.reference ??
    details?.payment_reference ??
    details?.description ??
    details?.ach_memo ??
    details?.trace_number ??
    details?.end_to_end_id ??
    details?.imad
  const senderAddress = event.data.senderAddresses?.map(sender => sender.address).find(Boolean)

  return {
    title: fiat ? 'Fiat deposit' : 'Crypto deposit',
    meta: fiat
      ? `${fiat.fiatCurrency} received into managed balance`
      : `${method.ticker} deposited on ${method.hostChain?.name ?? method.name}`,
    details: compactNotificationDetails([
      railLabel,
      senderName ? `From ${senderName}` : undefined,
      senderAccount ? `Account ${senderAccount}` : undefined,
      details?.sender_bank_name ?? details?.bank_name,
      reference ? `Ref ${reference}` : undefined,
      senderAddress ? `Sender ${senderAddress}` : undefined
    ]),
    amountLabel,
    tone: 'in',
    url: getNotificationUrl(event),
    unread,
    icon: getNotificationIcon(event),
    methodLogo: crypto ? method.logo : undefined
  }
}

const getWithdrawalNotificationItem = (
  event: Extract<RadomEvent, { name: 'Withdrawal' | 'Refund' }>,
  unread: boolean
): NotificationViewModel => {
  const destination = event.data.destination
  const fiatReceipt = event.data.receipt?.FiatReceipt
  const railLabel = destination || fiatReceipt
    ? formatFiatNotificationRailLabel(fiatReceipt?.transferMethod, destination?.paymentRail) ?? 'Bank transfer'
    : undefined
  const methodLabel = getPaymentMethodLabel(event.managedMethod)
  const title = event.name === 'Refund' ? 'Refund issued' : destination || fiatReceipt ? 'Fiat payout' : 'Crypto payout'

  return {
    title,
    meta: destination || fiatReceipt
      ? `${destination?.currency ?? event.managedMethod.ticker} sent via ${railLabel ?? 'bank transfer'}`
      : `Sent from managed balance via ${methodLabel}`,
    details: compactNotificationDetails([
      railLabel,
      destination?.traceNumber ? `Trace ${destination.traceNumber}` : undefined,
      destination?.uetr ? `UETR ${destination.uetr}` : undefined,
      destination?.externalAccountId ? `External account ${destination.externalAccountId}` : undefined,
      fiatReceipt?.withdrawalAccountId ? `Withdrawal account ${fiatReceipt.withdrawalAccountId}` : undefined
    ]),
    amountLabel: formatAssetNotificationAmount(event.data.withdrawalAmount, event.managedMethod.ticker, '-'),
    tone: 'out',
    url: getNotificationUrl(event),
    unread,
    icon: getNotificationIcon(event),
    methodLogo: destination || fiatReceipt ? undefined : event.managedMethod.logo
  }
}

const getSubscriptionNotificationItem = (
  event: Extract<RadomEvent, { name: 'NewSubscription' | 'SubscriptionCancelled' | 'SubscriptionExpired' }>,
  unread: boolean
): NotificationViewModel => {
  const statusLabel = event.name === 'NewSubscription'
    ? 'Created'
    : event.name === 'SubscriptionCancelled'
      ? 'Cancelled'
      : 'Expired'

  return {
    title: event.label,
    meta: `${event.managedMethod.ticker} recurring plan`,
    details: compactNotificationDetails([
      `Subscription ${statusLabel.toLowerCase()}`,
      event.data.subscriptionId ? `ID ${event.data.subscriptionId}` : undefined
    ]),
    amountLabel: event.name === 'NewSubscription'
      ? formatCurrency(Number(event.data.amount), event.data.currency)
      : statusLabel,
    tone: event.name === 'NewSubscription' ? 'blue' : 'neutral',
    url: getNotificationUrl(event),
    unread,
    icon: getNotificationIcon(event),
    methodLogo: event.managedMethod.logo
  }
}

export const getNotificationItem = (event: RadomEvent, latestManagedAck: Date): NotificationViewModel => {
  const unread = event.gateway === 'Managed' && event.date.getTime() > latestManagedAck.getTime()

  switch (event.name) {
  case 'PaymentEvent':
  case 'RecurringPaymentEvent':
  case 'IncompletePaymentEvent':
  case 'SubscriptionPaymentEvent':
  case 'OpenBanking':
    return getPaymentNotificationItem(event, unread)
  case 'Deposit':
    return getDepositNotificationItem(event, unread)
  case 'Withdrawal':
  case 'Refund':
    return getWithdrawalNotificationItem(event, unread)
  case 'NewSubscription':
  case 'SubscriptionCancelled':
  case 'SubscriptionExpired':
    return getSubscriptionNotificationItem(event, unread)
  default:
    return {
      title: event.label,
      meta: 'Notification',
      details: [],
      tone: 'neutral',
      url: event.url,
      unread,
      icon: getNotificationIcon(event)
    }
  }
}

const getNotificationToneColor = (tone: NotificationTone): string => {
  switch (tone) {
  case 'in':
    return '#11724f'
  case 'out':
    return '#9a5b00'
  case 'blue':
    return '#3157c6'
  case 'warning':
    return '#b45309'
  default:
    return '#687183'
  }
}

const getNotificationUrl = (event: RadomEvent): string => {
  const transactionReference = getTransactionReference(event)
  return transactionReference ? getTransactionActivityUrl(transactionReference) : event.url
}

const getNotificationIcon = (event: RadomEvent): React.ReactNode => {
  switch (event.name) {
  case 'Withdrawal':
    return <WithdrawalIcon />
  case 'Deposit':
    return <DepositIcon />
  case 'Refund':
    return <Refund />
  case 'NewSubscription':
    return <NewSubscription />
  case 'SubscriptionCancelled':
  case 'SubscriptionExpired':
  case 'IncompletePaymentEvent':
    return <SubscriptionRevoked />
  default:
    return <OrderIcon />
  }
}

interface NotificationViewModel {
  title: string
  meta: string
  details: string[]
  amountLabel?: string
  tone: NotificationTone
  url: string
  unread: boolean
  icon: React.ReactNode
  methodLogo?: string
}

const NON_FIAT_NOTIFICATION_RAIL_VALUES = new Set([
  'wallet',
  'crypto',
  'evm',
  'managed_balance',
  'managed balance'
])

const normalizeNotificationText = (value?: string | number | null): string | undefined => {
  if (value === undefined || value === null) return undefined
  const text = String(value).trim()
  return text.length > 0 ? text : undefined
}

const formatRailLabel = (value?: string): string | undefined => {
  const text = normalizeNotificationText(value)
  if (!text) return undefined

  const normalizedKey = text.toLowerCase().replace(/[\s-]+/g, '_')
  const railLabels: Record<string, string> = {
    ach: 'ACH',
    ach_push: 'ACH',
    ach_pull: 'ACH',
    fedwire: 'Wire',
    swift: 'SWIFT',
    sepa: 'SEPA',
    sepa_credit_transfer: 'SEPA',
    wire: 'Wire',
    faster_payments: 'Faster Payments'
  }

  if (railLabels[normalizedKey]) {
    return railLabels[normalizedKey]
  }

  return text
    .replace(/_/g, ' ')
    .replace(/-/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map(part => part.length <= 4 ? part.toUpperCase() : part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

const formatFiatNotificationRailLabel = (...values: Array<string | undefined>): string | undefined => {
  const meaningfulRail = values.find(value => {
    const text = normalizeNotificationText(value)
    if (!text) return false
    return !NON_FIAT_NOTIFICATION_RAIL_VALUES.has(text.toLowerCase().replace(/[\s-]+/g, '_'))
  })

  return formatRailLabel(meaningfulRail)
}

const inferDepositNotificationRailLabel = (event: Extract<RadomEvent, { name: 'Deposit' }>): string | undefined => {
  const details = event.data.beneficiaryDetails
  const explicitRail = formatFiatNotificationRailLabel(details?.payment_rail, details?.payment_scheme)
  if (explicitRail) return explicitRail
  if (!details) return undefined
  if (details.reference || details.iban || details.bic) return 'SEPA'
  if (details.description || details.ach_memo || details.trace_number || details.sender_bank_routing_number) return 'ACH'
  if (details.imad || details.wire_message || details.uetr) return 'Wire'
  return undefined
}

const getAssociatedEntityLabel = (entityType?: AssociatedEntityType): string => {
  switch (entityType) {
  case AssociatedEntityType.Invoice:
    return 'Invoice'
  case AssociatedEntityType.PaymentLink:
    return 'Payment link'
  case AssociatedEntityType.DonationLink:
    return 'Donation link'
  case AssociatedEntityType.CheckoutSession:
    return 'Checkout session'
  case AssociatedEntityType.PaymentSession:
    return 'Payment session'
  case AssociatedEntityType.Subscription:
    return 'Subscription'
  case AssociatedEntityType.OpenBanking:
    return 'Open banking'
  default:
    return 'Payment'
  }
}

const compactNotificationDetails = (details: Array<string | undefined>): string[] => {
  const seen = new Set<string>()
  return details
    .map(detail => normalizeNotificationText(detail))
    .filter((detail): detail is string => !!detail)
    .filter(detail => {
      const key = detail.toLowerCase()
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
    .slice(0, 4)
}

const formatAssetNotificationAmount = (amount: string | number, ticker: string, prefix = ''): string => {
  const numericAmount = Number(amount)
  const formatted = Number.isFinite(numericAmount)
    ? new Intl.NumberFormat('en-US', { maximumFractionDigits: 8 }).format(numericAmount)
    : String(amount)

  return `${prefix}${formatted} ${ticker}`
}

const getPaymentMethodLabel = (method: { ticker: string, hostChain?: { name: string }, name: string }): string =>
  method.hostChain ? `${method.ticker} on ${method.hostChain.name}` : method.ticker

export const getTransactionReference = (event: RadomEvent): string | undefined => {
  switch (event.name) {
  case 'PaymentEvent':
  case 'RecurringPaymentEvent':
  case 'IncompletePaymentEvent':
  case 'SubscriptionPaymentEvent':
  case 'OpenBanking':
    return event.id
  case 'Deposit':
    return event.data.depositId
  case 'Withdrawal':
  case 'Refund':
    return event.data.withdrawalRequestId
  case 'NewSubscription':
  case 'SubscriptionCancelled':
  case 'SubscriptionExpired':
    return event.data.subscriptionId
  default:
    return undefined
  }
}

export const NotificationDropdownShell = styled.div`
  width: min(430px, calc(100vw - 24px));
  max-height: 75vh;
  overflow: hidden;
  font-size: 14px;
  display: flex;
  align-items: stretch;
  flex-direction: column;
`

export const NotificationHeader = styled.div`
  width: 100%;
  padding: 18px 18px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-bottom: 1px solid #edf1f7;
`

export const NotificationHeaderTitle = styled.div`
  color: #20242d;
  font-size: 15px;
  font-weight: 600;
`

export const NotificationHeaderCount = styled.div`
  border: 1px solid #dbe2ee;
  border-radius: 999px;
  color: #687183;
  font-size: 12px;
  font-weight: 500;
  padding: 4px 9px;
  white-space: nowrap;
`

export const NotificationList = styled.div`
  width: 100%;
  overflow-y: auto;
`

export const NotificationEmpty = styled.div`
  color: ${RADOM_COLORS.GRAY_DARKEST};
  padding: 64px 20px;
  text-align: center;
`

export const Notification = styled.button`
  width: 100%;
  appearance: none;
  background: #fff;
  border: 0;
  border-bottom: 1px solid #edf1f7;
  color: inherit;
  cursor: pointer;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 12px;
  padding: 13px 16px;
  text-align: left;
  transition: 0.16s ease background-color;

  :hover {
    background-color: #f8fafc;
  }

  :active {
    opacity: 0.75;
  }

  :last-child {
    border-bottom: 0;
  }
`

export const NotificationIconBadge = styled.div<{ $tone: NotificationTone }>`
  align-items: center;
  display: inline-flex;
  height: 38px;
  justify-content: center;
  position: relative;
  width: 38px;

  svg {
    height: 28px;
    width: 28px;
  }
`

export const NotificationUnreadDot = styled.div`
  background: ${RADOM_COLORS.ORANGE};
  border: 2px solid #fff;
  border-radius: 999px;
  height: 10px;
  position: absolute;
  right: -2px;
  top: -2px;
  width: 10px;
`

export const NotificationBody = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
`

export const NotificationTitleRow = styled.div`
  align-items: flex-start;
  display: flex;
  gap: 12px;
  justify-content: space-between;
`

export const NotificationTitle = styled.div`
  color: #20242d;
  font-size: 13.5px;
  font-weight: 600;
  line-height: 1.3;
  min-width: 0;
`

export const NotificationWhen = styled.div`
  color: #8b92a2;
  flex: 0 0 auto;
  font-size: 11.5px;
  line-height: 1.35;
  padding-top: 1px;
  white-space: nowrap;
`

export const NotificationMeta = styled.div`
  color: #687183;
  font-size: 12.5px;
  line-height: 1.45;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const NotificationDetails = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  min-width: 0;
`

export const NotificationDetailPill = styled.div`
  background: #f4f6fa;
  border: 1px solid #e7ebf3;
  border-radius: 999px;
  color: #687183;
  font-size: 11.5px;
  line-height: 1.2;
  max-width: 100%;
  overflow: hidden;
  padding: 4px 7px;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const NotificationAmount = styled.div<{ $tone: NotificationTone }>`
  color: ${({ $tone }) => getNotificationToneColor($tone)};
  font-size: 12.5px;
  font-weight: 600;
  line-height: 1.35;
  margin-top: 1px;
`

export const NotificationMethodLogo = styled.img`
  border-radius: 999px;
  height: 15px;
  margin-right: 5px;
  object-fit: contain;
  vertical-align: -2px;
  width: 15px;
`
